import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/api';
import RoleBasedSidebar from '../../components/RoleBasedSidebar';
import AuthContext from '../../contexts/AuthContext';
import '../../styles/ConversationsListPage.css';
import '../../styles/DashboardLayout.css';

const ConversationsListPage = () => {
  const { user } = useContext(AuthContext);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Determine if user is a parent - used for message creation link
  const isParent = user?.role === 'parent';

  // Toggle sidebar
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Close sidebar
  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Define fetchConversations outside useEffect so it can be called from the retry button
  const fetchConversations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the correct API endpoint for conversations as seen in messageService.js
      const response = await api.get('/messages/conversations');
      
      console.log('Conversations API response:', response);
      
      if (response.data) {
        // Handle different response formats
        let conversationsData;
        if (response.data.success !== undefined) {
          // Standard API response format with success property
          conversationsData = response.data.success ? response.data.data : [];
        } else if (Array.isArray(response.data)) {
          // Direct array response
          conversationsData = response.data;
        } else if (typeof response.data === 'object' && response.data.data) {
          // Object with data property
          conversationsData = Array.isArray(response.data.data) ? response.data.data : [];
        } else {
          // Unknown format, default to empty array
          conversationsData = [];
        }
        
        console.log('Processed conversations data:', conversationsData);
        setConversations(conversationsData);
      } else {
        setConversations([]);
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
      if (err.response) {
        // Server returned an error response
        setError(`Failed to load conversations: ${err.response.data?.message || err.response.status}. Please try again later.`);
      } else if (err.request) {
        // The request was made but no response was received
        setError('Unable to connect to the server. Please check your internet connection and try again.');
      } else {
        // Something else went wrong
        setError('An unexpected error occurred. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Use effect to call it on component mount
  useEffect(() => {
    fetchConversations();
  }, []);

  // Helper function to get conversation name
  const getConversationName = (conversation) => {
    // For group conversations, use the title
    if (conversation.type === 'group' && conversation.title) {
      return conversation.title;
    }

    // For individual conversations, find the other participant
    if (conversation.participants && conversation.participants.length > 0) {
      // Get current user from localStorage
      const userData = localStorage.getItem('user');
      let currentUserId = '';
      if (userData) {
        try {
          const user = JSON.parse(userData);
          currentUserId = user._id || user.id || '';
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }

      // Find the other participant (not the current user)
      const otherParticipant = conversation.participants.find(
        p => (p._id !== currentUserId) && (p.id !== currentUserId)
      );

      // If we found the other participant, use their name
      if (otherParticipant) {
        return otherParticipant.name || 'Unknown'; 
      }
      
      // If no other participant found or the other participant is the user themselves,
      // try to find someone with a different role (i.e., teacher if user is parent)
      const userRole = user?.role || '';
      const participantWithDifferentRole = conversation.participants.find(
        p => p.role && p.role !== userRole && p.role !== 'parent'
      );

      if (participantWithDifferentRole) {
        return participantWithDifferentRole.name || 'Unknown';
      }
    }

    return 'Unknown';
  };

  // Filter conversations based on search input
  const filteredConversations = conversations.filter(conversation => {
    const participantName = getConversationName(conversation);
    const lastMessage = conversation.lastMessage ? conversation.lastMessage.content : '';

    return (
      participantName.toLowerCase().includes(filter.toLowerCase()) ||
      lastMessage.toLowerCase().includes(filter.toLowerCase())
    );
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      // Today - show time
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInDays === 1) {
      // Yesterday
      return 'Yesterday';
    } else if (diffInDays < 7) {
      // Last week - show day name
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      // Older - show date
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className="dashboard-container">
      {/* Mobile Header - without hamburger menu */}
      <div className="mobile-header">
        <h1 className="app-title">Messages</h1>
        <div className="avatar">👤</div>
      </div>

      {/* Dashboard Layout */}
      <div className="dashboard-layout">
        {/* Sidebar */}
        <RoleBasedSidebar isOpen={sidebarOpen} onClose={closeSidebar} />
        
        {/* Main Content */}
        <div className="dashboard-content">
          <div className="conversations-list-container">
            <div className="header">
              <div className="header-left">
                <Link to="/dashboard" className="back-to-dashboard">
                  &larr; Back to Dashboard
                </Link>
                <h1>Messages</h1>
              </div>
              <Link to={isParent ? "/parent/messages/new" : "/messages/new"} className="new-message-btn">
                New Message
              </Link>
            </div>

      <div className="search-container">
        <input
          type="text"
          placeholder="Search messages..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="search-input"
        />
      </div>

      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading conversations...</p>
        </div>
      ) : error ? (
        <div className="error">
          <div className="error-icon">❌</div>
          <p>{error}</p>
          <button
            className="retry-button"
            onClick={fetchConversations}
          >
            Try Again
          </button>
        </div>
      ) : (
        <div className="conversations-list">
          {filteredConversations.length > 0 ? (
            filteredConversations.map(conversation => (
              <Link
                to={`/messages/${conversation._id}?type=${conversation.type || 'individual'}&halaqaId=${conversation.halaqa || ''}`}
                key={conversation._id}
                className={`conversation-item ${conversation.unreadCount > 0 ? 'unread' : ''}`}
                onClick={() => {
                  // Store conversation data in sessionStorage for the details page
                  sessionStorage.setItem('currentConversation', JSON.stringify({
                    id: conversation._id,
                    type: conversation.type || 'individual',
                    title: getConversationName(conversation),
                    halaqaId: conversation.halaqa || null,
                    halaqa: conversation.halaqa || null
                  }));
                }}
              >
                <div className="avatar">
                  {getConversationName(conversation).charAt(0).toUpperCase()}
                </div>
                <div className="conversation-content">
                  <div className="conversation-header">
                    <span className="participant-name">
                      {getConversationName(conversation)}
                    </span>
                    <span className="timestamp">
                      {conversation.lastMessage
                        ? formatDate(conversation.lastMessage.createdAt)
                        : ''}
                    </span>
                  </div>
                  <div className="last-message">
                    {conversation.lastMessage
                      ? conversation.lastMessage.content
                      : 'No messages yet'}
                  </div>
                </div>
                {conversation.unreadCount > 0 &&
                  <div className="unread-indicator">
                    {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
                  </div>
                }
              </Link>
            ))
          ) : (
            <div className="empty-state">
              {filter 
                ? 'No conversations matching your search' 
                : 'No conversations yet'}
              <p>
                <Link to={isParent ? "/parent/messages/new" : "/messages/new"} className="start-conversation-btn">
                  Start a conversation
                </Link>
              </p>
            </div>
          )}
        </div>
      )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationsListPage;